/**
 * ProvisioningCoordinator
 *
 * Root-level coordinator (pavilion-only) that orchestrates the host-side
 * provisioning execution flow. Receives scripts from the realtime WebSocket,
 * queues them locally, and executes them sequentially via Playjector.
 *
 * The host is a pure passthrough: script content and signature are never modified.
 *
 * Pattern: Coordinator -> Managers (ScriptQueueManager, ScriptExecutionManager)
 */

import {
  ProvisioningMessageType,
  generateProvisioningMessage,
  type ProvisioningMessage,
  type WebsocketMessage,
  type PlaycastMessageSystemLaunchElevatedPowerShellScriptReply,
  type PlaycastMessageSystemLaunchElevatedPowerShellScriptReceivedReply,
  type PlaycastMessageSystemCancelProvisioningScriptReply,
} from '@playcastdotio/Messaging';
import { USE_PROVISIONING_COORDINATOR, USE_MAINTENANCE_SESSION_DISPATCH } from '../../config/featureFlags';
import type { IMessageRouter } from '../../services/MessageRouter';
import { ScriptQueueManager, type QueuedScript } from './managers/ScriptQueueManager';
import { ScriptExecutionManager } from './managers/ScriptExecutionManager';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import { ipcReceive } from '../../services/IpcMessaging';
import { MaintenanceNativeRequestService } from '../../services/maintenance/MaintenanceActivityService';
import { HostMaintenanceSessionNativeService, MaintenanceSessionManager } from './managers/MaintenanceSessionManager';
import type { MaintenanceSessionDispatch } from '@playcastdotio/Messaging';

interface ProvisioningCoordinatorDeps {
  ipcService: IIpcService;
  realtimeService: IRealtimeService;
  getMachineId: () => string;
  getHostId: () => string;
}

export class ProvisioningCoordinator {
  private readonly queueManager: ScriptQueueManager;
  private readonly executionManager: ScriptExecutionManager;
  private readonly realtimeService: IRealtimeService;
  private readonly getMachineId: () => string;
  private isProcessing = false;
  private readonly maintenanceRequests?: MaintenanceNativeRequestService;
  private readonly maintenanceSession?: MaintenanceSessionManager;

  constructor(deps: ProvisioningCoordinatorDeps) {
    this.realtimeService = deps.realtimeService;
    this.getMachineId = deps.getMachineId;
    this.queueManager = new ScriptQueueManager();
    this.executionManager = new ScriptExecutionManager({
      ipcService: deps.ipcService,
      realtimeService: deps.realtimeService,
      getMachineId: deps.getMachineId,
      getHostId: deps.getHostId,
      ownership: this.queueManager,
    });
    if (USE_MAINTENANCE_SESSION_DISPATCH) {
      this.maintenanceRequests = new MaintenanceNativeRequestService({ ipcService: deps.ipcService, receive: ipcReceive });
      this.maintenanceSession = new MaintenanceSessionManager({
        native: new HostMaintenanceSessionNativeService(this.maintenanceRequests), ownership: this.queueManager,
        machineIdentity: { getMachineId: deps.getMachineId }, enabled: true,
        onDisconnected: listener => deps.realtimeService.onDisconnected?.(listener) ?? (() => undefined),
      });
    }
  }

  init(): void {
    this.maintenanceSession?.init();
    console.log('[ProvisioningCoordinator] Initialized (enabled=%s)', USE_PROVISIONING_COORDINATOR);
  }

  dispose(): void {
    this.maintenanceSession?.dispose();
    this.maintenanceRequests?.dispose();
  }

  // ========================================
  // Public API - Registration (Setup Only)
  // ========================================

  /**
   * Register WebSocket message handlers with the realtime message router.
   * Called once during app initialization.
   */
  registerMessageHandlers(router: IMessageRouter): void {
    router.register(ProvisioningMessageType.PROVISIONING_MAINTENANCE_DEPLOY, message => {
      if (!this.maintenanceSession) return;
      void this.maintenanceSession.handleDispatch(message as unknown as MaintenanceSessionDispatch)
        .catch(() => console.warn('[ProvisioningCoordinator] Signed maintenance dispatch requires native reconciliation'));
    });
    router.register(
      ProvisioningMessageType.PROVISIONING_SCRIPT_DEPLOY,
      (msg) => this.onScriptDeploy(msg as ProvisioningMessage),
    );
    router.register(
      ProvisioningMessageType.PROVISIONING_SCRIPT_REVOKE,
      (msg) => this.onScriptRevoke(msg as ProvisioningMessage),
    );
  }

  /**
   * Register Playjector IPC reply handlers with the Playjector message router.
   * Called once during app initialization.
   */
  registerPlayjectorHandlers(router: IMessageRouter): void {
    router.register(
      'launchElevatedPowerShellScript',
      (msg) => this.onPlayjectorReply(msg as unknown as PlayjectorReplyRouterMessage),
    );
    router.register('launchElevatedPowerShellScriptReceived', (msg) =>
      this.executionManager.handlePlayjectorAck(
        msg as unknown as PlaycastMessageSystemLaunchElevatedPowerShellScriptReceivedReply['message'],
      ),
    );
    router.register('cancelProvisioningScript', (msg) =>
      this.executionManager.handleCancelReply(
        msg as unknown as PlaycastMessageSystemCancelProvisioningScriptReply['message'],
      ),
    );
  }

  // ========================================
  // Private - WebSocket Message Handlers
  // ========================================

  private onScriptDeploy(message: ProvisioningMessage): void {
    if (!USE_PROVISIONING_COORDINATOR) return;
    if (message.type !== ProvisioningMessageType.PROVISIONING_SCRIPT_DEPLOY) return;

    const script: QueuedScript = {
      deploymentId: message.deploymentId,
      executionId: message.executionId,
      scriptId: message.scriptId,
      scriptName: message.scriptName,
      scriptType: message.scriptType,
      completeScript: message.completeScript,
      signature: message.signature,
      pauseMatchmaking: message.pauseMatchmaking,
      deploymentMode: message.deploymentMode,
      timeoutSeconds: message.timeoutSeconds,
      stepIndex: message.stepIndex,
      stepName: message.stepName,
      stepCount: message.stepCount,
    };

    console.log(
      '[ProvisioningCoordinator] Script deploy received: deployment=%s execution=%s name="%s"',
      script.deploymentId,
      script.executionId,
      script.scriptName,
    );

    this.queueManager.enqueue(script);
    this.sendQueueStatus();
    this.processQueue();
  }

  private onScriptRevoke(message: ProvisioningMessage): void {
    if (!USE_PROVISIONING_COORDINATOR) return;
    if (message.type !== ProvisioningMessageType.PROVISIONING_SCRIPT_REVOKE) return;

    type RevokeMessage = Extract<
      ProvisioningMessage,
      { type: typeof ProvisioningMessageType.PROVISIONING_SCRIPT_REVOKE }
    >;
    const revokeMessage = message as RevokeMessage;

    const { deploymentId, reason } = revokeMessage;
    const forceKill = revokeMessage.forceKill === true;
    const exitGuestMode = revokeMessage.exitGuestMode !== false;
    const rebootIfSafe = revokeMessage.rebootIfSafe !== false;

    console.log(
      '[ProvisioningCoordinator] Script revoke received: deployment=%s reason=%s forceKill=%s',
      deploymentId,
      reason ?? 'none',
      forceKill,
    );

    const removed = this.queueManager.revoke(deploymentId);

    if (this.queueManager.isExecutingDeployment(deploymentId)) {
      if (forceKill) {
        console.warn(
          '[ProvisioningCoordinator] Deployment %s is currently executing; sending cancel to Playjector (exitGuestMode=%s rebootIfSafe=%s)',
          deploymentId,
          exitGuestMode,
          rebootIfSafe,
        );
        this.executionManager.cancelExecution({ exitGuestMode, rebootIfSafe });
      } else {
        console.warn(
          '[ProvisioningCoordinator] Deployment %s is currently executing; forceKill not requested, cannot stop in-progress script',
          deploymentId,
        );
      }
    }

    if (removed === 0 && !this.queueManager.isExecutingDeployment(deploymentId)) {
      console.log('[ProvisioningCoordinator] No queued scripts found for deployment=%s', deploymentId);
    }

    this.sendQueueStatus();
  }

  // ========================================
  // Private - Playjector IPC Reply Handler
  // ========================================

  private onPlayjectorReply(message: PlayjectorReplyRouterMessage): void {
    this.executionManager.handlePlayjectorReply(message);
  }

  // ========================================
  // Private - Queue Status Reporting
  // ========================================

  private sendQueueStatus(): void {
    try {
      const message = generateProvisioningMessage(
        ProvisioningMessageType.PROVISIONING_QUEUE_STATUS,
        {
          machineId: this.getMachineId(),
          pendingCount: this.queueManager.getPendingCount(),
          isExecuting: this.queueManager.isCurrentlyExecuting(),
          currentDeploymentId: this.queueManager.getCurrentExecution()?.deploymentId,
        },
      );
      this.realtimeService.sendMessage(message);
    } catch (err) {
      console.error('[ProvisioningCoordinator] Failed to send queue status:', err);
    }
  }

  // ========================================
  // Private - Queue Processing
  // ========================================

  private processQueue(): void {
    if (this.isProcessing) return;

    const next = this.queueManager.dequeueNext();
    if (!next) return;

    this.isProcessing = true;
    this.executeScript(next);
  }

  private async executeScript(script: QueuedScript): Promise<void> {
    try {
      const result = await this.executionManager.execute(script);
      console.log(
        '[ProvisioningCoordinator] Script execution complete: deployment=%s status=%s exitCode=%s',
        script.deploymentId,
        result.status,
        result.exitCode ?? 'N/A',
      );
    } catch (err) {
      console.error('[ProvisioningCoordinator] Unexpected execution error:', err);
    } finally {
      this.queueManager.markComplete();
      this.isProcessing = false;
      this.sendQueueStatus();
      this.processQueue();
    }
  }
}

/**
 * Shape of the Playjector reply body as routed through playjectorMessageRouter.
 * The IPC handler wraps the reply body with a `type` field for router dispatch.
 */
type PlayjectorReplyRouterMessage = PlaycastMessageSystemLaunchElevatedPowerShellScriptReply['message'] & {
  type: string;
};
